import * as React from 'react';
import {
  Box,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  styled,
} from '@mui/material';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import stockTickers from '../../../../../public/assets/search/stock_paths.json';

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2a2d64',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2a2d64',
    fontWeight: 600,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    padding: '7px 15px 5px 15px',
  },
}));

const tickerSet = new Set(
  Array.isArray(stockTickers)
    ? stockTickers.map((e) => e?.t).filter(Boolean)
    : [],
);

const formatPercentage = (str) => {
  if (str == null) return '';
  const num = parseFloat(String(str).replace('%', '').trim());
  if (Number.isNaN(num)) return '';
  return `${num.toFixed(1)} %`;
};

const formatNumberWithSuffix = (str) => {
  if (str == null) return '';
  const value = String(str);
  const suffixMatch = value.match(/[a-zA-Z]+$/);
  const suffix = suffixMatch ? suffixMatch[0] : '';
  const num = parseFloat(value.replace(/,/g, '').replace(suffix, '').trim());
  if (Number.isNaN(num)) return '';
  return `${num.toFixed(1)} ${suffix}`;
};

const parseData = (data) => {
  if (data == null) return null;
  if (Array.isArray(data)) return data;
  if (typeof data !== 'string') return null;
  try {
    const parsed = JSON.parse(data);
    return Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    console.error('Error parsing data:', error);
    return null;
  }
};

const WinnerAndLoserTable = ({ data, label, value, tableName, isPct, color, isMobile }) => {
  const dataArray = React.useMemo(() => parseData(data), [data]);

  if (data === undefined || data === null) {
    return <Box component="div">No data available.</Box>;
  }

  if (!dataArray || dataArray.length === 0) {
    return <Box component="div" />;
  }

  return (
    <Table
      size="small"
      stickyHeader
      sx={{
        '& th': { color: '#666' },
        '& .th-1': { textAlign: 'right' },
        '& th:nth-of-type(1), td:nth-of-type(1)': { pr: 0 },
        '& th:nth-of-type(2), td:nth-of-type(2)': { pl: 0 },
        borderRadius: '8px',
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell
            className="td-right"
            sx={{
              fontSize: '.875rem',
              bgcolor: '#fbfbfc',
              color: '#555 !important',
              fontWeight: isMobile ? 500 : 600,
            }}
            colSpan={2}
          >
            {isMobile ? 'Ticker' : tableName}
          </TableCell>
          <TableCell
            className="td-right"
            sx={{
              fontSize: '.9rem',
              bgcolor: '#fbfbfc',
              textAlign: 'right',
              color: '#555 !important',
              fontWeight: 600,
            }}
          >
            {isPct ? '%' : '$'}
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody
        sx={{
          '& .MuiTableRow-root:nth-last-of-type(1) td': { borderBottom: 0 },
          '& th': { whiteSpace: 'nowrap' },
          '& td:nth-of-type(1)': { pr: 1 },
          '& td:nth-of-type(2)': { pl: 1 },
        }}
      >
        {dataArray.map((d, i) => {
          const ticker = d?.ticker;
          const hasLink = !!ticker && tickerSet.has(ticker);
          const issuerName = d?.[label];
          return (
            <TableRow key={ticker ?? i} sx={{ bgcolor: '#fff' }}>
              <TableCell
                className="px-2 text-capitalize"
                sx={{
                  fontSize: '.9rem',
                  fontWeight: 500,
                  textAlign: 'left',
                  color: '#174ea6',
                  '& a': { color: '#174ea6' },
                  maxWidth: '3.7rem',
                }}
              >
                {hasLink ? (
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`/stock/${ticker}`}
                    underline="hover"
                  >
                    {ticker}
                  </Link>
                ) : (
                  ticker
                )}
              </TableCell>
              <TableCell
                className="px-2 text-capitalize"
                sx={{
                  fontSize: '.9rem',
                  fontWeight: 500,
                  textAlign: 'left',
                  color: '#444',
                  maxWidth: '147px',
                }}
              >
                <LightTooltip title={issuerName ?? ''} disableInteractive>
                  <div
                    style={{
                      textTransform: 'capitalize',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {issuerName?.toLowerCase()}
                  </div>
                </LightTooltip>
              </TableCell>
              <TableCell
                className="px-2 text-capitalize"
                sx={{
                  fontSize: '.9rem',
                  fontWeight: 600,
                  textAlign: 'right',
                  color,
                  whiteSpace: 'nowrap',
                }}
              >
                {isPct ? formatPercentage(d?.[value]) : formatNumberWithSuffix(d?.[value])}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default WinnerAndLoserTable;
